import React from "react";

export interface YesNoDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  yesText?: string;
  noText?: string;
  defaultButton?: 'yes' | 'no';
  dialogBgColor?: string;
  yesBgColor?: string;
  noBgColor?: string;
  yesTextColor?: string;
  noTextColor?: string;
  noButtonVisible?: boolean;
  onYes: () => void;
  onNo: () => void;
  onClose?: () => void;
}

const YesNoDialog: React.FC<YesNoDialogProps> = ({
  isOpen,
  title,
  message,
  yesText = "Yes",
  noText = "No",
  defaultButton = 'yes',
  dialogBgColor = "bg-white dark:bg-gray-800",
  yesBgColor = "bg-cuno-500 hover:bg-cuno-700 dark:bg-blue-500 dark:hover:bg-blue-600",
  noBgColor = "bg-gray-600 hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600",
  yesTextColor = "text-white",
  noTextColor = "text-white",
  noButtonVisible = true,
  onYes,
  onNo,
  onClose
}) => {
  if (!isOpen) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (defaultButton === 'yes') {
        onYes();
      } else {
        onNo();
      }
    } else if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className={`relative w-full max-w-md mx-4 rounded-lg shadow-lg ${dialogBgColor}`}>
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Title */}
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {title}
          </h2>

          {/* Message */}
          <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
            {message}
          </p>

          {/* Buttons */}
          <div className="flex gap-3 justify-end">
            {noButtonVisible &&<button
              onClick={onNo}
              className={`px-6 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${noBgColor} ${noTextColor} ${
                defaultButton === 'no' 
                  ? 'ring-2 ring-blue-500 dark:ring-blue-400' 
                  : ''
              }`}
              autoFocus={defaultButton === 'no'}
            >
              {noText}
            </button>}
            <button
              onClick={onYes}
              className={`px-6 py-2 text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${yesBgColor} ${yesTextColor} ${
                defaultButton === 'yes' 
                  ? 'ring-2 ring-blue-500 dark:ring-blue-400' 
                  : ''
              }`}
              autoFocus={defaultButton === 'yes'}
            >
              {yesText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YesNoDialog;